import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// The page that asks it. Named here so the guard below can let it through
// without sending it to itself.
const ACCOUNT_TYPE_PATH = '/account-type';

export default function ProtectedRoute({ children }) {
  const { user, profile, loading } = useAuth();
  const location = useLocation();

  // Firebase restores a persisted session ASYNCHRONOUSLY: on a fresh page load
  // `user` is null for the first moments even when the visitor is signed in,
  // and only becomes real once onAuthStateChanged fires. Redirecting during
  // that window kicked signed-in users to /login on every refresh (and made a
  // just-registered account look like it had "logged itself out" a second
  // later). Waiting for `loading` to clear is what distinguishes "not signed
  // in" from "not known yet".
  if (loading) return null;

  if (!user) {
    // Pass the attempted URL so we can redirect back after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // ── ONE QUESTION STILL OWED ────────────────────────────────────────────────
  // A Google sign-up never sees the registration form, so nobody asked whether
  // it is a customer or a designer account; `userTypePending` on the user
  // document is what records that (see createUserDocumentIfNew).
  //
  // THE CHECK BELONGS HERE, not on the button that signs people in. Those know
  // about it too and route straight to the page, which is what keeps the common
  // path free of a redirect — but they cannot cover a sign-up abandoned
  // halfway, a tab closed on the question, or the guest flow, where registering
  // happens inside a modal that then navigates somewhere of its own. This is
  // every signed-in page, so there is nowhere left to slip through.
  //
  // `profile` is null while it loads, so this waits rather than assuming — an
  // eager redirect would land every user on the chooser for a frame on every
  // single page view.
  //
  // The attempted URL rides along, exactly as it does for the login redirect
  // above, so answering returns the user to where they were going instead of
  // dropping them on the dashboard.
  if (profile?.userTypePending && location.pathname !== ACCOUNT_TYPE_PATH) {
    return <Navigate to={ACCOUNT_TYPE_PATH} state={{ from: location }} replace />;
  }

  return children;
}
